import React from 'react';
import PropTypes from 'prop-types';
import { PlayCircle, FileBarChart } from 'lucide-react';

/**
 * OverallProgressCard - Displays overall assessment score with gradient design
 * Shows score, gap zone label, progress bar, and action buttons
 * @param {Object} assessment - Assessment object with overall_score and answered_questions
 * @param {number} totalQuestions - Total number of questions in assessment
 * @param {number} progressPercent - Completion percentage (0-100)
 * @param {number} overallProgressAnimationKey - Key to trigger progress bar animation
 * @param {boolean} viewingHistoricalAssessment - Whether viewing a historical assessment
 * @param {function} onStartAssessment - Handler to start/continue assessment
 * @param {function} onViewSummary - Handler to view assessment summary
 */
const OverallProgressCard = ({
  assessment,
  totalQuestions,
  progressPercent,
  overallProgressAnimationKey,
  viewingHistoricalAssessment,
  onStartAssessment,
  onViewSummary
}) => {
  /**
   * Gets gap zone label and styling based on score
   * @param {number} score - Overall score percentage
   * @returns {Object} Object with label and className
   */
  const getGapZoneDisplay = (score) => {
    if (score > 86) {
      return {
        label: 'No Gaps',
        className: 'bg-[#c49e73] text-[#8b6f47]'
      };
    } else if (score > 72) {
      return {
        label: 'Minor Gaps',
        className: 'bg-[#a7d5a8] text-green-800'
      };
    } else if (score > 57) {
      return {
        label: 'Considerable Gaps',
        className: 'bg-[#b8d4e8] text-blue-800'
      };
    } else if (score > 43) {
      return {
        label: 'Critical Gaps',
        className: 'bg-[#f4ebb0] text-yellow-800'
      };
    } else if (score > 28) {
      return {
        label: 'Very Critical Gaps',
        className: 'bg-[#f5d7b3] text-orange-800'
      };
    } else {
      return {
        label: 'Extremely Critical',
        className: 'bg-[#f5c9c9] text-red-800'
      };
    }
  };

  const gapZone = getGapZoneDisplay(assessment.overall_score);
  const isComplete = assessment.answered_questions >= totalQuestions;

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
      {/* Header Section */}
      <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 px-4 sm:px-8 pt-4 sm:pt-6 pb-6 sm:pb-8 text-white relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24"></div>

        <div className="relative">
          {/* Title and Info */}
          <div className="mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-3xl font-bold mb-2">Overall Assessment Score</h2>
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-blue-100">
              <span className="text-xs sm:text-sm font-medium">
                {assessment.answered_questions} of {totalQuestions} questions answered
              </span>
              <span className="text-sm hidden sm:inline">•</span>
              <span className="text-xs sm:text-sm font-medium">{Math.round(progressPercent)}% complete</span>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {/* Score Display */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 sm:p-6 border border-white/20">
              <div className="text-center">
                <div className="text-4xl sm:text-6xl font-bold mb-2 sm:mb-3 leading-none">
                  {Math.round(assessment.overall_score)}%
                </div>
                <div className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold shadow-lg ${gapZone.className}`}>
                  {gapZone.label}
                </div>
                <div className="mt-2 sm:mt-3 text-xs text-blue-100 font-medium">Business Gap Assessment Score</div>
              </div>
            </div>

            {/* Progress & Actions */}
            <div className="md:col-span-2 space-y-3 sm:space-y-4">
              {/* Progress Bar */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 sm:p-6 border border-white/20">
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <span className="text-xs sm:text-sm font-semibold">Assessment Progress</span>
                  <span className="text-xs sm:text-sm font-bold">{Math.round(progressPercent)}%</span>
                </div>
                <div className="relative bg-white/20 rounded-full h-3 sm:h-4 overflow-hidden shadow-inner">
                  <div
                    key={`progress-${overallProgressAnimationKey}`}
                    className="h-full rounded-full bg-gradient-to-r from-green-400 to-emerald-500 shadow-lg"
                    style={{
                      width: 0,
                      animation: `growWidth 2000ms cubic-bezier(0.4, 0, 0.2, 1) forwards`,
                      animationFillMode: 'forwards',
                      '--final-width': `${progressPercent}%`
                    }}
                  ></div>
                </div>
                <div className="mt-2 text-xs text-blue-100">
                  {isComplete
                    ? 'Assessment complete!'
                    : `${totalQuestions - assessment.answered_questions} questions remaining`}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {!isComplete && !viewingHistoricalAssessment ? (
                  <button
                    onClick={onStartAssessment}
                    className="flex items-center justify-center space-x-1 sm:space-x-2 px-3 sm:px-5 py-2 sm:py-3 bg-white text-blue-600 rounded-xl hover:bg-blue-50 hover:scale-105 font-bold transition-all shadow-lg text-sm"
                  >
                    <PlayCircle size={16} />
                    <span>Continue</span>
                  </button>
                ) : (
                  <div className="flex items-center justify-center px-3 sm:px-5 py-2 sm:py-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                    <span className="text-xs sm:text-sm font-semibold text-white">Assessment Complete</span>
                  </div>
                )}

                <button
                  onClick={onViewSummary}
                  className="flex items-center justify-center space-x-1 sm:space-x-2 px-3 sm:px-5 py-2 sm:py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 hover:scale-105 font-bold transition-all shadow-lg text-sm"
                >
                  <FileBarChart size={16} />
                  <span>View Summary</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

OverallProgressCard.propTypes = {
  assessment: PropTypes.shape({
    overall_score: PropTypes.number.isRequired,
    answered_questions: PropTypes.number.isRequired
  }).isRequired,
  totalQuestions: PropTypes.number.isRequired,
  progressPercent: PropTypes.number.isRequired,
  overallProgressAnimationKey: PropTypes.number.isRequired,
  viewingHistoricalAssessment: PropTypes.bool.isRequired,
  onStartAssessment: PropTypes.func.isRequired,
  onViewSummary: PropTypes.func.isRequired
};

export default OverallProgressCard;
